import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  TestTube, Plus, Trash2, Pencil, Download, FileText,
  FileSpreadsheet, Loader2, Upload, Save, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { haccpApi, pdvApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import HaccpConsultantHeader from '../components/HaccpConsultantHeader';

const EDITOR_ROLES = ['super_admin', 'admin', 'amministratore', 'rspp'];

const emptySample = () => ({
  tipo_prodotto: '',
  lotto: '',
  scadenza: '',
  quantita: '',
  conservazione: '',
  note: '',
});

const emptyForm = () => ({
  pdv_id: '',
  titolo: '',
  data_prelievo: new Date().toISOString().slice(0, 10),
  ora_prelievo: '10:00',
  luogo_prelievo: '',
  laboratorio: '',
  motivo: '',
  esito_visivo: '',
  responsabile_ul: '',
  campioni: [emptySample()],
  note: '',
  template_id: '',
});

export default function HaccpSamplesPage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const canEdit = EDITOR_ROLES.includes(user?.role);

  const [tab, setTab] = useState('records');
  const [pdvList, setPdvList] = useState([]);
  const [records, setRecords] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [preferredId, setPreferredId] = useState(null);
  const [labUsers, setLabUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [uploadDialog, setUploadDialog] = useState(false);
  const [tplFile, setTplFile] = useState(null);
  const [tplName, setTplName] = useState('');
  const [tplDefault, setTplDefault] = useState(true);
  const [uploadingTpl, setUploadingTpl] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r, t, prefs, labs] = await Promise.all([
        pdvApi.getAll(),
        haccpApi.listRecords({ kind: 'campioni' }),
        haccpApi.listTemplates('campioni'),
        haccpApi.getPreferences().catch(() => ({ data: {} })),
        api.get('/users/role/laboratorio_analisi').catch(() => ({ data: [] })),
      ]);
      setPdvList(p.data);
      setRecords(r.data);
      setTemplates(t.data);
      setPreferredId(prefs?.data?.campioni || null);
      setLabUsers(labs.data || []);
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    const next = emptyForm();
    if (currentPdvId && pdvList.some(p => p.id === currentPdvId)) {
      next.pdv_id = currentPdvId;
    } else if (pdvList.length === 1) {
      next.pdv_id = pdvList[0].id;
    }
    if (preferredId && templates.find(t => t.id === preferredId)) {
      next.template_id = preferredId;
    } else if (templates.length > 0) {
      const def = templates.find(t => t.is_default) || templates[0];
      next.template_id = def.id;
    }
    setForm(next);
    setDialog(true);
  };

  const handleSetPreferred = async (templateId) => {
    try {
      const newId = preferredId === templateId ? null : templateId;
      await haccpApi.setPreference('campioni', newId);
      setPreferredId(newId);
      toast.success(newId ? 'Impostato come preferito' : 'Preferenza rimossa');
    } catch (e) {
      toast.error('Errore');
    }
  };

  const openEdit = async (rec) => {
    try {
      const full = await haccpApi.getRecord(rec.id);
      const d = full.data;
      setForm({
        pdv_id: d.pdv_id || '',
        titolo: d.titolo || '',
        data_prelievo: d.data_prelievo || '',
        ora_prelievo: d.ora_prelievo || '',
        luogo_prelievo: d.luogo_prelievo || '',
        laboratorio: d.laboratorio || '',
        motivo: d.motivo || '',
        esito_visivo: d.esito_visivo || '',
        responsabile_ul: d.responsabile_ul || '',
        campioni: (d.campioni || []).map(c => ({ ...emptySample(), ...c })),
        note: d.note || '',
        template_id: d.template_id || '',
      });
      if (form.campioni.length === 0) {
        // ensure at least one
      }
      setEditingId(rec.id);
      setDialog(true);
    } catch (e) {
      toast.error('Errore apertura record');
    }
  };

  const handleSave = async () => {
    if (!form.pdv_id) { toast.error('Seleziona UL'); return; }
    if ((form.campioni || []).length === 0) { toast.error('Aggiungi almeno un campione'); return; }
    setSaving(true);
    try {
      const payload = {
        kind: 'campioni',
        pdv_id: form.pdv_id,
        titolo: form.titolo || null,
        data_prelievo: form.data_prelievo || null,
        ora_prelievo: form.ora_prelievo || null,
        luogo_prelievo: form.luogo_prelievo || null,
        laboratorio: form.laboratorio || null,
        motivo: form.motivo || null,
        esito_visivo: form.esito_visivo || null,
        responsabile_ul: form.responsabile_ul || null,
        campioni: form.campioni,
        note: form.note || null,
        template_id: form.template_id || null,
      };
      if (editingId) {
        await haccpApi.updateRecord(editingId, payload);
        toast.success('Verbale aggiornato');
      } else {
        await haccpApi.createRecord(payload);
        toast.success('Verbale creato');
      }
      setDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo verbale?')) return;
    try {
      await haccpApi.deleteRecord(id);
      toast.success('Eliminato');
      load();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleAssignLab = async (rec) => {
    if (labUsers.length === 0) {
      toast.error('Nessun Laboratorio Analisi registrato. Vai a Gestione Utenti per crearne uno.');
      return;
    }
    const list = labUsers.map((l, i) => `${i + 1}. ${l.full_name} (${l.email})`).join('\n');
    const choice = window.prompt(`Assegna verbale al Laboratorio Analisi:\n\n${list}\n\nInserisci il numero:`, '1');
    if (!choice) return;
    const idx = parseInt(choice, 10) - 1;
    if (idx < 0 || idx >= labUsers.length) { toast.error('Scelta non valida'); return; }
    try {
      await api.put(`/haccp-records/${rec.id}/assign-lab`, { lab_user_id: labUsers[idx].id });
      toast.success(`Verbale assegnato a ${labUsers[idx].full_name}. Email inviata.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const handleUploadTemplate = async () => {
    if (!tplFile || !tplName) { toast.error('Compila nome e file .docx'); return; }
    setUploadingTpl(true);
    try {
      await haccpApi.uploadTemplate(tplFile, tplName, 'campioni', tplDefault);
      toast.success('Template caricato');
      setUploadDialog(false);
      setTplFile(null); setTplName(''); setTplDefault(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally {
      setUploadingTpl(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await haccpApi.deleteTemplate(id);
      toast.success('Template eliminato');
      load();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const addSample = () => setForm((p) => ({ ...p, campioni: [...p.campioni, emptySample()] }));
  const removeSample = (i) => setForm((p) => ({ ...p, campioni: p.campioni.filter((_, idx) => idx !== i) }));
  const setSample = (i, patch) => setForm((p) => {
    const next = [...p.campioni];
    next[i] = { ...next[i], ...patch };
    return { ...p, campioni: next };
  });

  const pdvName = (id) => pdvList.find(p => p.id === id)?.name || '—';

  return (
    <div className="p-6 space-y-6" data-testid="haccp-campioni-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <TestTube className="w-7 h-7 text-purple-500" />
            HACCP — Verbale Prelievo Campioni
          </h1>
          <p className="text-gray-600 mt-1">
            Documenta prelievi di campioni alimentari per analisi. Output: <strong>DOCX</strong>, <strong>PDF</strong> e <strong>Excel</strong>.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="new-sample-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo Verbale
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="records" data-testid="tab-records">Verbali Compilati</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Template Word</TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : records.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              Nessun verbale. {canEdit && 'Crea il primo cliccando "Nuovo Verbale".'}
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {records.map((r) => (
                <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-base">{r.titolo || `Verbale ${pdvName(r.pdv_id)}`}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Badge variant="outline" className="mb-2">{pdvName(r.pdv_id)}</Badge>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>📅 {r.data_prelievo} {r.ora_prelievo}</div>
                        <div>🧪 {(r.campioni || []).length} campioni</div>
                        {r.laboratorio && <div>🔬 {r.laboratorio}</div>}
                        {r.assigned_lab_name && <div className="text-emerald-600 font-medium">✅ Assegnato a: {r.assigned_lab_name}</div>}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button size="sm" variant="outline" onClick={() => window.open(haccpApi.docxUrl(r.id), '_blank')} data-testid={`docx-${r.id}`}>
                          <FileText className="w-4 h-4 mr-1" /> DOCX
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(haccpApi.pdfUrl(r.id), '_blank')} data-testid={`pdf-${r.id}`}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(haccpApi.excelUrl(r.id), '_blank')} data-testid={`xlsx-${r.id}`}>
                          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                        </Button>
                        {canEdit && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleAssignLab(r)} data-testid={`assign-lab-${r.id}`} title="Assegna a Laboratorio Analisi">
                              <TestTube className="w-4 h-4 mr-1" /> {r.assigned_lab_name ? 'Cambia Lab' : 'Assegna Lab'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Placeholder utilizzabili nel template <code className="bg-muted px-1 rounded">.docx</code>: <code>«Nome_Azienda»</code>, <code>«Data_Prelievo»</code>, <code>«Ora_Prelievo»</code>, <code>«Luogo_Prelievo»</code>, <code>«Motivo_Prelievo»</code>, <code>«Laboratorio»</code>, <code>«Responsabile_UL»</code>, <code>«Consulente_HACCP»</code>, <code>«Luogo_Data_Firma»</code>, <code>«Firma_Consulente»</code>. Per la tabella campioni: marker <code>«CAMPIONE_ROW»</code> + colonne <code>«tipo_prodotto»</code>, <code>«lotto»</code>, <code>«scadenza»</code>, <code>«quantita»</code>, <code>«conservazione»</code>, <code>«note»</code>.
            </p>
            {canEdit && (
              <Button onClick={() => setUploadDialog(true)} data-testid="upload-template-btn">
                <Upload className="w-4 h-4 mr-1" /> Carica Template
              </Button>
            )}
          </div>

          {templates.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              Nessun template caricato.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {templates.map((t) => (
                <Card key={t.id}>
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        {t.name}
                        {t.is_default && <Badge>Default</Badge>}
                        {preferredId === t.id && <Badge className="bg-amber-100 text-amber-700 border-amber-300">⭐ Preferito</Badge>}
                        {!t.file_exists && <Badge variant="destructive">File mancante</Badge>}
                      </div>
                      <div className="text-xs text-gray-500">{t.filename} • {Math.round((t.size_bytes || 0) / 1024)} KB</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetPreferred(t.id)}
                        title={preferredId === t.id ? 'Rimuovi preferito' : 'Imposta come preferito (solo per te)'}
                        data-testid={`prefer-${t.id}`}
                      >
                        <Star className={`w-4 h-4 ${preferredId === t.id ? 'text-amber-500 fill-amber-500' : ''}`} />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => handleDeleteTemplate(t.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* === Record Dialog === */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="sample-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Verbale Prelievo' : 'Nuovo Verbale Prelievo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label>Unità Locale *</Label>
                <Select value={form.pdv_id} onValueChange={(v) => setForm({ ...form, pdv_id: v })}>
                  <SelectTrigger data-testid="ul-select"><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                  <SelectContent>
                    {pdvList.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Titolo verbale</Label>
                <Input value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="es. Prelievo a campione" />
              </div>
              <div>
                <Label>Data prelievo</Label>
                <Input type="date" value={form.data_prelievo} onChange={(e) => setForm({ ...form, data_prelievo: e.target.value })} />
              </div>
              <div>
                <Label>Ora prelievo</Label>
                <Input type="time" value={form.ora_prelievo} onChange={(e) => setForm({ ...form, ora_prelievo: e.target.value })} />
              </div>
              <div className="lg:col-span-2">
                <Label>Luogo prelievo</Label>
                <Input value={form.luogo_prelievo} onChange={(e) => setForm({ ...form, luogo_prelievo: e.target.value })} placeholder="es. cella frigorifera, banco vetrina, magazzino" />
              </div>
              <div className="lg:col-span-2">
                <Label>Laboratorio di destinazione</Label>
                <Input value={form.laboratorio} onChange={(e) => setForm({ ...form, laboratorio: e.target.value })} placeholder="es. Lab. Analisi Microbio S.r.l." />
              </div>
              <div className="lg:col-span-2">
                <Label>Motivo del prelievo</Label>
                <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="es. autocontrollo periodico" />
              </div>
              <div className="lg:col-span-2">
                <Label>Esito visivo</Label>
                <Input value={form.esito_visivo} onChange={(e) => setForm({ ...form, esito_visivo: e.target.value })} placeholder="es. conforme / odore anomalo" />
              </div>
              <div className="lg:col-span-2">
                <Label>Responsabile UL presente</Label>
                <Input value={form.responsabile_ul} onChange={(e) => setForm({ ...form, responsabile_ul: e.target.value })} placeholder="Nome Cognome" />
              </div>
              <div className="lg:col-span-2">
                <Label>Template DOCX</Label>
                <Select value={form.template_id || '__default__'} onValueChange={(v) => setForm({ ...form, template_id: v === '__default__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Default disponibile</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Campioni prelevati ({form.campioni.length})</h3>
                <Button size="sm" variant="outline" onClick={addSample} data-testid="add-sample-btn">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="p-2 w-8">#</th>
                      <th className="p-2 min-w-[200px]">Tipo prodotto</th>
                      <th className="p-2 min-w-[140px]">Lotto</th>
                      <th className="p-2 min-w-[130px]">Scadenza</th>
                      <th className="p-2 min-w-[100px]">Quantità</th>
                      <th className="p-2 min-w-[180px]">Conservazione</th>
                      <th className="p-2 min-w-[180px]">Note</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.campioni.map((c, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1 text-gray-500">{i + 1}</td>
                        <td className="p-1"><Input value={c.tipo_prodotto} onChange={(e) => setSample(i, { tipo_prodotto: e.target.value })} placeholder="es. mozzarella" /></td>
                        <td className="p-1"><Input value={c.lotto} onChange={(e) => setSample(i, { lotto: e.target.value })} /></td>
                        <td className="p-1"><Input type="date" value={c.scadenza} onChange={(e) => setSample(i, { scadenza: e.target.value })} /></td>
                        <td className="p-1"><Input value={c.quantita} onChange={(e) => setSample(i, { quantita: e.target.value })} placeholder="es. 250 g" /></td>
                        <td className="p-1"><Input value={c.conservazione} onChange={(e) => setSample(i, { conservazione: e.target.value })} placeholder="es. +4 °C refrigerato" /></td>
                        <td className="p-1"><Input value={c.note} onChange={(e) => setSample(i, { note: e.target.value })} /></td>
                        <td className="p-1">
                          <Button size="sm" variant="ghost" onClick={() => removeSample(i)} disabled={form.campioni.length <= 1}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <Label>Note generali</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-sample-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salva Verbale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Upload Template Dialog === */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent data-testid="upload-template-dialog">
          <DialogHeader>
            <DialogTitle>Carica Template DOCX — Verbale Campioni</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome template *</Label>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="es. Verbale Prelievo Standard" />
            </div>
            <div>
              <Label>File .docx *</Label>
              <Input type="file" accept=".docx" onChange={(e) => setTplFile(e.target.files?.[0] || null)} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={tplDefault} onChange={(e) => setTplDefault(e.target.checked)} />
              Imposta come default per Verbale Campioni
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUploadDialog(false)}>Annulla</Button>
            <Button onClick={handleUploadTemplate} disabled={uploadingTpl}>
              {uploadingTpl ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
